import React, { useState, useEffect, useCallback } from 'react';
import GooeyNav from '../components/GooeyNav';
import AIChatWidget from '../components/AIChatWidget';
import RotatingText from '../components/RotatingText';
import { useNavigate } from 'react-router-dom';
import { auth, db, requestFCMToken, onForegroundMessage } from '../firebase';
import { collection, query, where, onSnapshot, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import axios from 'axios';
import { getAvailableSlots, bookSlotWithTransaction, releaseSlot } from '../utils/slotManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  MapPin, Search, Star, Clock, Calendar, LogOut, Navigation, Map, User, Heart, 
  Bell, TrendingUp, Sparkles, Camera, ChevronRight, X, Phone, AlertTriangle,
  CheckCircle, Image, Scissors, Timer, HelpCircle
} from 'lucide-react';
import LocationPickerModal from '../components/LocationPickerModal';
import NearbySalonsMap from '../components/NearbySalonsMap';

// Use relative path for Firebase Functions
const API = '/api';

const categories = [
  { id: 'haircut', name: 'Haircut', icon: '✂️' },
  { id: 'makeup', name: 'Makeup', icon: '💄' },
  { id: 'facial', name: 'Facial', icon: '🧖' },
  { id: 'spa', name: 'Spa', icon: '💆' },
  { id: 'bridal', name: 'Bridal', icon: '👰' }
];

// ── Why Choose Us Slider ──────────────────────────────────────
const WHY_SLIDES = [
  { emoji: '⚡', title: 'Instant Booking', desc: 'Book any salon in seconds, no calls needed.', color: 'from-red-500 to-rose-400' },
  { emoji: '📍', title: 'Nearby Salons', desc: 'Discover top-rated salons close to you.', color: 'from-blue-500 to-indigo-400' },
  { emoji: '💰', title: 'Best Offers', desc: 'Exclusive daily deals & discounts.', color: 'from-green-500 to-emerald-400' },
  { emoji: '⭐', title: 'Verified Reviews', desc: 'Real ratings from real customers.', color: 'from-yellow-500 to-amber-400' },
  { emoji: '🔒', title: 'Secure Payments', desc: 'Your money is always protected.', color: 'from-purple-500 to-violet-400' },
];

function WhyChooseUsSlider({ sliderIndex, setSliderIndex }) {
  const touchStart = React.useRef(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setSliderIndex(i => (i + 1) % WHY_SLIDES.length);
    }, 3200);
    return () => clearInterval(timer);
  }, [setSliderIndex]);

  const prev = () => setSliderIndex(i => (i - 1 + WHY_SLIDES.length) % WHY_SLIDES.length);
  const next = () => setSliderIndex(i => (i + 1) % WHY_SLIDES.length);

  return (
    <div className="mb-6 animate-fade-up">
      <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
        <span className="text-lg">✨</span> Why Choose Us?
      </h2>
      <div
        className="relative overflow-hidden rounded-2xl"
        onTouchStart={e => { touchStart.current = e.touches[0].clientX; }}
        onTouchEnd={e => {
          const dx = touchStart.current - e.changedTouches[0].clientX;
          if (dx > 50) next();
          if (dx < -50) prev();
        }}
      >
        {/* Track */}
        <div
          style={{ display: 'flex', transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)', transform: `translateX(-${sliderIndex * 100}%)` }}
        >
          {WHY_SLIDES.map((slide, i) => (
            <div
              key={i}
              style={{ minWidth: '100%' }}
              className={`bg-gradient-to-r ${slide.color} p-5 text-white rounded-2xl`}
            >
              <div className="flex items-center gap-4">
                <div className="text-4xl">{slide.emoji}</div>
                <div>
                  <h3 className="font-bold text-lg leading-tight">{slide.title}</h3>
                  <p className="text-white/85 text-sm mt-0.5">{slide.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Prev / Next arrows */}
        <button
          onClick={prev}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/25 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-sm transition-all"
        >❮</button>
        <button
          onClick={next}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/25 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-sm transition-all"
        >❯</button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-1.5 mt-2.5">
        {WHY_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setSliderIndex(i)}
            className={`transition-all duration-300 rounded-full ${
              i === sliderIndex ? 'w-5 h-2 bg-red-500' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function CustomerHome() {
  const navigate = useNavigate();
  const [userGender, setUserGender] = useState(localStorage.getItem('userGender') || 'all');
  const [location, setLocation] = useState({ lat: null, lng: null, address: 'Choose Your Location' });
  const [searchQuery, setSearchQuery] = useState('');
  const [radius] = useState(5);
  const [salons, setSalons] = useState([]);
  const [filteredSalons, setFilteredSalons] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [bookings, setBookings] = useState([]);
  const [showBookings, setShowBookings] = useState(false);
  const [sortBy, setSortBy] = useState('rating');
  const [loading, setLoading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  const [trendingSalons, setTrendingSalons] = useState([]);
  const [recommendedSalons, setRecommendedSalons] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [bookingReminder, setBookingReminder] = useState(null);
  const [showReminderBanner, setShowReminderBanner] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, review_text: '' });
  const [salonReviews, setSalonReviews] = useState([]);
  const [showSalonDetails, setShowSalonDetails] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [serviceReminders, setServiceReminders] = useState([]);
  const [currentLockId, setCurrentLockId] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = React.useRef(null);

  // Spam protection
  const [recentBookingTimes, setRecentBookingTimes] = useState([]);

  const handleGenderChange = (gender) => {
    const g = gender === 'all' ? 'all' : gender;
    setUserGender(g);
    localStorage.setItem('userGender', g);
    filterAndSortSalons();
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
    loadSavedLocation();
    fetchFavorites();
    checkUpcomingReminders();
    fetchTrendingSalons();
    fetchRecommendedSalons();
    fetchServiceReminders();
  }, []);

  // Check reminders every minute
  useEffect(() => {
    const interval = setInterval(checkUpcomingReminders, 60000);
    return () => clearInterval(interval);
  }, []);

  // REAL-TIME Firestore Listener for Customer Bookings
  // Wrapped in onAuthStateChanged so listener only starts AFTER Firebase
  // auth is fully resolved — avoids the "Missing permissions" error that
  // fires when the component mounts before the auth token is ready.
  useEffect(() => {
    let firestoreUnsubscribe = null;

    const authUnsubscribe = auth.onAuthStateChanged((user) => {
      // Tear down any existing Firestore listener first
      if (firestoreUnsubscribe) {
        firestoreUnsubscribe();
        firestoreUnsubscribe = null;
      }

      if (!user?.phoneNumber) return;

      const phone = user.phoneNumber.replace('+91', '');
      
      // Fetch bookings from MongoDB API instead of Firestore
      const fetchBookingsFromAPI = async () => {
        try {
          const resp = await axios.get(`${API}/bookings/customer/${phone}`);
          const data = Array.isArray(resp.data) ? resp.data : [];
          setBookings(data);
        } catch(e) {
          console.warn('API bookings fetch failed:', e.message);
        }
      };
      fetchBookingsFromAPI();
      const pollingInterval = setInterval(fetchBookingsFromAPI, 15000);
      firestoreUnsubscribe = () => clearInterval(pollingInterval);

      // NOTE: Firestore realtime listener removed — it caused
      // "Missing or insufficient permissions" errors because customers
      // don't have read access to the bookings collection by rule.
      // API polling above (every 15s) + FCM push handle all updates.
    });

    return () => {
      authUnsubscribe();
      if (firestoreUnsubscribe) firestoreUnsubscribe();
    };
  }, []);

  // ── Register customer for push notifications ──────────────
  useEffect(() => {
    const phone = localStorage.getItem('userPhone');
    if (!phone) return;

    (async () => {
      try {
        const token = await requestFCMToken();
        if (token) {
          await axios.post(`${API}/fcm/save-token`, { token, user_phone: phone });
          console.log('Customer FCM token registered');
        }
      } catch (e) {
        console.warn('FCM registration skipped:', e.message);
      }
    })();

    // Foreground notification — show toast + play sound
    const unsub = onForegroundMessage((payload) => {
      const title = payload?.notification?.title || payload?.data?.title || 'Booking Update';
      const body  = payload?.notification?.body  || payload?.data?.body  || '';

      toast.success(title, { description: body, duration: 8000 });

      // Native notification with system sound
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(title, {
            body,
            icon: '/logo192.png',
            silent: false,
            tag: `booking-${payload?.data?.bookingId || Date.now()}`,
          });
        }
      } catch (e) { /* ignore */ }

      // Refresh bookings list so status updates immediately
      fetchBookings();
    });

    return () => { if (typeof unsub === 'function') unsub(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll reveal for static sections
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [salons]);

  const fetchTrendingSalons = async () => {
    try {
      const response = await axios.get(`${API}/salons/trending`);
      setTrendingSalons(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching trending salons:', error);
    }
  };

  const fetchRecommendedSalons = async () => {
    try {
      const phone = localStorage.getItem('userPhone');
      if (!phone) return;
      const response = await axios.get(`${API}/salons/recommended?customer_phone=${phone}`);
      setRecommendedSalons(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching recommended salons:', error);
    }
  };

  const fetchServiceReminders = async () => {
    try {
      const phone = localStorage.getItem('userPhone');
      if (!phone) return;
      const response = await axios.get(`${API}/customer/${phone}/service-reminders`);
      setServiceReminders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching service reminders:', error);
    }
  };

  const checkUpcomingReminders = async () => {
    try {
      const phone = localStorage.getItem('userPhone');
      if (!phone) return;
      
      const response = await axios.get(`${API}/bookings/upcoming-reminders/${phone}`);
      const reminders = Array.isArray(response.data) ? response.data : [];
      
      if (reminders.length > 0) {
        const urgent = reminders.find(r => r.is_urgent);
        if (urgent) {
          setBookingReminder(urgent);
          setShowReminderBanner(true);
          // Show toast for urgent reminders
          toast.warning(
            `Your appointment at ${urgent.salon_name} starts in ${urgent.minutes_away} minutes!`,
            { duration: 10000 }
          );
        }
      }
    } catch (error) {
      console.error('Error checking reminders:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const firebaseUid = localStorage.getItem('firebaseUid');
      if (!firebaseUid) return;
      const response = await axios.get(`${API}/customer/${firebaseUid}/favorites`);
      setFavorites(Array.isArray(response.data) ? response.data.map(s => s.salon_id) : []);
    } catch (error) {
      setFavorites([]);
    }
  };

  const toggleFavorite = async (salonId, e) => {
    e.stopPropagation();
    try {
      const firebaseUid = localStorage.getItem('firebaseUid');
      if (!firebaseUid) {
        toast.error('Please login to save favorites');
        return;
      }
      
      await axios.post(`${API}/customer/${firebaseUid}/favorites/${salonId}`);
      
      if (favorites.includes(salonId)) {
        setFavorites(favorites.filter(id => id !== salonId));
        toast.success('Removed from favorites');
      } else {
        setFavorites([...favorites, salonId]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  useEffect(() => {
    if (location.lat && location.lng) {
      searchSalons();
    }
  }, [location]);

  useEffect(() => {
    filterAndSortSalons();
  // eslint-disable-next-line
  }, [salons, searchQuery, selectedCategory, sortBy]);

  const loadSavedLocation = () => {
    const savedLocation = localStorage.getItem('customerLocation');
    if (savedLocation) {
      try {
        const parsed = JSON.parse(savedLocation);
        setLocation(parsed);
      } catch (e) {
        setLocation({ lat: null, lng: null, address: 'Choose Your Location' });
      }
    }
  };

  const handleLocationSelect = (selectedLocation) => {
    try {
      if (!selectedLocation || typeof selectedLocation.lat !== 'number' || typeof selectedLocation.lng !== 'number') {
        toast.error('Invalid location. Please try again.');
        return;
      }

      const newLocation = {
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        address: selectedLocation.address || `${Number(selectedLocation.lat).toFixed(6)}, ${Number(selectedLocation.lng).toFixed(6)}`
      };
      
      setLocation(newLocation);
      localStorage.setItem('customerLocation', JSON.stringify(newLocation));
      toast.success('Location saved successfully!');
    } catch (error) {
      toast.error('Failed to save location. Please try again.');
    }
  };

  const searchSalons = async () => {
    if (!location.lat || !location.lng) return;

    setLoading(true);

    // Cold start can take 3-6s, so allow generous timeout + retries
    const attempt = async (timeoutMs) => {
      // `area` lets the backend match salons that were registered before
      // the location picker existed and therefore have no coordinates.
      const areaName = (location.address || '').split(',')[0].trim();
      const resp = await axios.get(`${API}/salons/nearby`, {
        params: { lat: location.lat, lng: location.lng, radius, area: areaName },
        timeout: timeoutMs,
      });
      return Array.isArray(resp.data) ? resp.data : [];
    };

    try {
      const data = await attempt(15000);
      console.log(`[Salons] Loaded ${data.length} salons within ${radius}km`);
      setSalons(data);
    } catch (err1) {
      console.warn('[Salons] First attempt failed:', err1.message);
      try {
        // Second attempt — backend should be warm now
        const data = await attempt(20000);
        console.log(`[Salons] Retry loaded ${data.length} salons`);
        setSalons(data);
      } catch (err2) {
        console.error('[Salons] Both attempts failed:', err2.message);
        setSalons([]);
        toast.error('Could not load nearby salons', {
          description: 'Check your connection and tap refresh.',
          duration: 5000,
        });
      }
    }
    setLoading(false);
  };

  // Smart Search with debounce — shows instant "Searching..." feedback
  const handleSmartSearch = (query) => {
    if (!query || query.length < 2) {
      setShowSearchResults(false);
      setSearchResults([]);
      setIsSearching(false);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      return;
    }

    // Show searching indicator immediately
    setIsSearching(true);
    setShowSearchResults(true);

    // Debounce actual API call by 300ms
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/search`, {
          params: {
            q: query,
            lat: location.lat || undefined,
            lng: location.lng || undefined,
          },
          timeout: 12000,
        });
        setSearchResults(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        // Timeouts on the very first (cold-start) search are expected and harmless;
        // the salon list still loads separately. Log quietly, don't alarm.
        if (error?.code !== 'ECONNABORTED') {
          console.error('Search error:', error);
        }
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const filterAndSortSalons = () => {
    let filtered = Array.isArray(salons) ? [...salons] : [];

    // Filter by business type — only when explicitly selected
    if (userGender === 'male') {
      filtered = filtered.filter(salon => {
        if (!salon) return false;
        return salon.business_type === 'salon' || salon.business_type === 'unisex' || !salon.business_type;
      });
    } else if (userGender === 'female') {
      filtered = filtered.filter(salon => {
        if (!salon) return false;
        return salon.business_type === 'parlour' || salon.business_type === 'unisex' || !salon.business_type;
      });
    }
    // 'all' or no gender — show everything

    if (searchQuery && filtered.length > 0) {
      filtered = filtered.filter(salon => {
        if (!salon) return false;
        const nameMatch = salon.salon_name?.toLowerCase().includes(searchQuery.toLowerCase());
        const areaMatch = salon.area?.toLowerCase().includes(searchQuery.toLowerCase());
        const serviceMatch = Array.isArray(salon.services) && salon.services.some(s => s?.name?.toLowerCase().includes(searchQuery.toLowerCase()));
        return nameMatch || areaMatch || serviceMatch;
      });
    }

    if (selectedCategory !== 'all' && filtered.length > 0) {
      filtered = filtered.filter(salon => {
        if (!salon) return false;
        return Array.isArray(salon.services) && salon.services.some(s => s?.name?.toLowerCase().includes(selectedCategory));
      });
    }

    filtered.sort((a, b) => {
      if (sortBy === 'rating') return (b?.rating || 4.0) - (a?.rating || 4.0);
      if (sortBy === 'price') {
        const aPrice = Array.isArray(a?.services) && a.services[0]?.price ? a.services[0].price : 0;
        const bPrice = Array.isArray(b?.services) && b.services[0]?.price ? b.services[0].price : 0;
        return aPrice - bPrice;
      }
      return 0;
    });

    setFilteredSalons(filtered);
  };

  const fetchSlots = async (salonId, date) => {
    if (!selectedSalon) return;
    try {
      const formattedDate = date ? new Date(date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      
      console.log('🎯 Fetching slots for:', { salonId, date: formattedDate, salon: selectedSalon });
      
      // Use new slot manager
      const slots = await getAvailableSlots(
        salonId,
        formattedDate,
        selectedSalon.opening_time || '09:00',
        selectedSalon.closing_time || '20:00'
      );
      
      console.log('✅ Fetched slots:', slots);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('❌ Error fetching slots:', error);
      setAvailableSlots([]);
    }
  };

  // Auto-refresh slots every 30s when booking dialog open
  useEffect(() => {
    if (!showBooking || !selectedSalon) return;
    const interval = setInterval(() => {
      const date = selectedDate
        ? new Date(selectedDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];
      fetchSlots(selectedSalon.salon_id, date);
    }, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line
  }, [showBooking, selectedSalon?.salon_id, selectedDate]);

  const fetchSalonReviews = async (salonId) => {
    try {
      const response = await axios.get(`${API}/salon/${salonId}/reviews`);
      setSalonReviews(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setSalonReviews([]);
    }
  };

  const handleViewSalonDetails = (salon) => {
    setSelectedSalon(salon);
    fetchSalonReviews(salon.salon_id);
    setShowSalonDetails(true);
  };

  const handleBookService = (salon, service) => {
    setSelectedSalon(salon);
    setSelectedService(service);
    setShowBooking(true);
    setShowSalonDetails(false);
    
    // Auto-fetch slots for today if no date selected
    const dateToUse = selectedDate || new Date().toISOString().split('T')[0];
    if (!selectedDate) setSelectedDate(dateToUse);
    
    console.log('🎫 Opening booking dialog, fetching slots for:', dateToUse);
    fetchSlots(salon.salon_id, dateToUse);
  };

  const handleDateChange = (date) => {
    // Ensure date is in YYYY-MM-DD format
    const formattedDate = date ? new Date(date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setSelectedDate(formattedDate);
    setSelectedSlot(''); // Reset slot selection on date change
    setCurrentLockId(null);
    if (selectedSalon) fetchSlots(selectedSalon.salon_id, formattedDate);
  };

  // Select slot (no locking needed, transaction handles it)
  const handleSlotSelect = async (slotTime) => {
    if (!selectedSalon || !selectedDate || !slotTime) return;
    
    const timeStr = typeof slotTime === 'object' ? (slotTime.time || '') : String(slotTime);
    if (!timeStr) return;
    
    setSelectedSlot(timeStr);
  };

  const createBooking = async () => {
    if (!selectedSlot || !paymentMethod) {
      toast.error('Please select slot and payment method');
      return;
    }

    const customerPhone = localStorage.getItem('userPhone') || '';
    const customerName = localStorage.getItem('userName') || 'Customer';
    const formattedDate = selectedDate ? new Date(selectedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

    setLoading(true);
    try {
      // Use Firestore transaction for slot booking
      const bookingData = {
        salonId: selectedSalon.salon_id,
        salonName: selectedSalon.salon_name || selectedSalon.name || '',
        customerName: customerName,
        customerPhone: customerPhone,
        serviceName: selectedService.name || '',
        servicePrice: selectedService.price || 0,
        paymentMethod: paymentMethod
      };

      // Create booking via API with Firebase Auth token
      let authToken = '';
      try {
        authToken = await auth.currentUser?.getIdToken(true) || '';
      } catch(e) {}

      const mongoResponse = await axios.post(`${API}/booking/create`, {
        salon_id: selectedSalon.salon_id,
        salon_name: selectedSalon.salon_name || selectedSalon.name || '',
        service_id: selectedService.id || '',
        service_name: selectedService.name || '',
        service_price: selectedService.price || 0,
        slot_time: String(selectedSlot),
        booking_date: formattedDate,
        payment_method: paymentMethod,
        customer_phone: customerPhone,
        customer_name: customerName
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      const mongoBookingId = mongoResponse.data?.booking_id || mongoResponse.data?.id || '';

      // Firestore sync removed - MongoDB is the single source of truth

      // Build a ready-to-send WhatsApp message so the customer can instantly
      // message the salon to confirm — free, no API needed.
      const salonPhone = String(
        selectedSalon.phone || selectedSalon.owner_phone || selectedSalon.contact || ''
      ).replace(/\D/g, '');

      if (salonPhone) {
        const waNumber = salonPhone.length === 10 ? `91${salonPhone}` : salonPhone;
        const waText = encodeURIComponent(
          `Namaste! Maine BookYourSalons se booking ki hai.\n` +
          `Salon: ${selectedSalon.salon_name || selectedSalon.name || ''}\n` +
          `Service: ${selectedService.name || ''}\n` +
          `Date: ${formattedDate}\n` +
          `Time: ${selectedSlot}\n` +
          `Naam: ${customerName || ''}\n` +
          (mongoBookingId ? `Booking ID: ${mongoBookingId}` : '')
        );
        const waLink = `https://wa.me/${waNumber}?text=${waText}`;

        toast.success('Booking confirmed! Waiting for salon approval.', {
          duration: 8000,
          action: {
            label: '📱 Salon ko WhatsApp karein',
            onClick: () => window.open(waLink, '_blank'),
          },
        });
      } else {
        toast.success('Booking confirmed! Waiting for salon approval.');
      }

      setShowBooking(false);
      resetBookingForm();
    } catch (error) {
      console.error('Error creating booking:', error);

      // Handle subscription expired error specifically
      const errMsg = error?.response?.data?.error || error?.response?.data?.error_hi || error.message || '';
      const errHi = error?.response?.data?.error_hi || '';

      if (error?.response?.status === 403) {
        toast.error('Booking unavailable', {
          description: errHi || errMsg || 'This salon is not accepting bookings right now.',
          duration: 6000,
        });
      } else if (error?.response?.status === 400 && errMsg.includes('booked')) {
        toast.error('Slot already booked!', {
          description: 'Please select a different time slot.',
          duration: 5000,
        });
        // Refresh slots
        fetchSlots(selectedSalon.salon_id, formattedDate);
      } else {
        toast.error('Booking failed. Please try again.', {
          description: errMsg,
          duration: 4000,
        });
      }
    }
    setLoading(false);
  };

  const resetBookingForm = () => {
    setSelectedSlot('');
    setPaymentMethod('');
    setSelectedService(null);
    setSelectedSalon(null);
  };

  const fetchBookings = async () => {
    const phone = localStorage.getItem('userPhone');
    if (!phone) {
      setBookings([]);
      setShowBookings(true);
      return;
    }
    try {
      const response = await axios.get(`${API}/bookings/customer/${phone}`);
      const bookingsData = Array.isArray(response.data) ? response.data : [];
      setBookings(bookingsData);
      setShowBookings(true);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
      setShowBookings(true);
    }
  };

  const cancelBooking = async (bookingId) => {
    try {
      // Find booking to get slot info
      const booking = bookings.find(b => 
        (b.bookingId === bookingId || b.booking_id === bookingId || b.id === bookingId)
      );
      
      // Release slot in Firestore
      if (booking) {
        await releaseSlot(
          booking.salonId || booking.salon_id,
          booking.date || booking.booking_date,
          booking.slotTime || booking.slot_time
        );
      }
      
      // Cancel in MongoDB
      await axios.patch(`${API}/booking/${bookingId}/cancel`);
      
      // Update Firestore booking status
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, { status: 'cancelled' });
      
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const submitReview = async () => {
    if (!selectedSalon) return;
    
    try {
      await axios.post(`${API}/review/create`, {
        salon_id: selectedSalon.salon_id,
        customer_phone: localStorage.getItem('userPhone'),
        customer_name: localStorage.getItem('userName') || 'Customer',
        rating: reviewData.rating,
        review_text: reviewData.review_text
      });
      toast.success('Review submitted!');
      setShowReviewDialog(false);
      setReviewData({ rating: 5, review_text: '' });
      fetchSalonReviews(selectedSalon.salon_id);
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const handleLogout = () => {
    auth.signOut();
    localStorage.clear();
    navigate('/');
  };

  const getSalonStatusBadge = (salon) => {
    const status = salon.current_status || 'open';
    if (status === 'closed') {
      return <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">CLOSED</span>;
    }
    if (status === 'fully_booked') {
      return <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">FULLY BOOKED</span>;
    }
    return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">OPEN</span>;
  };

  return (
    <div className="min-h-screen" style={{background:'#f8f5ff'}}>
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>

      {/* Reminder Banner */}
      {showReminderBanner && bookingReminder && (
        <div style={{background:'linear-gradient(135deg,#f59e0b,#d97706)'}} className="text-white px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 animate-pulse" />
            <span className="text-xs font-semibold">
              Appointment at {bookingReminder.salon_name} in {bookingReminder.minutes_away} min!
            </span>
          </div>
          <button onClick={() => setShowReminderBanner(false)} className="text-white/80 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Premium Sticky Navbar */}
      <nav className="nav-glass sticky top-0 z-[100]">
        <div className="container mx-auto px-3 sm:px-4 py-2.5">
          <div className="flex items-center justify-between gap-2">
            {/* Left: Logo + Location */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <div className="bg-gradient-to-br from-red-500 to-rose-600 p-1.5 rounded-lg shadow-sm">
                  <Scissors className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-black text-gray-900 hidden sm:block" style={{fontFamily:"'Syne',sans-serif"}}>
                  Book<span className="text-red-600">Your</span>Salons
                </span>
              </div>
              <button
                data-testid="choose-location-btn"
                onClick={() => setShowLocationPicker(true)}
                className="flex items-center gap-1.5 bg-gray-50 hover:bg-red-50 border border-gray-200 hover:border-red-300 rounded-xl px-2.5 py-1.5 transition-all max-w-[140px] sm:max-w-[200px]"
              >
                <MapPin className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 truncate">
                  {location.lat ? (location.address?.split(',')[0] || 'Location Set') : 'Choose Location'}
                </span>
                {location.lat && <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" />}
              </button>
            </div>

            {/* Right: Action buttons */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {location.lat && (
                <button
                  onClick={() => setShowMapView(!showMapView)}
                  data-testid="toggle-map-btn"
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                    showMapView
                      ? 'bg-red-600 text-white shadow-md'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <Map className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Map</span>
                </button>
              )}
              <button
                onClick={fetchBookings}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 transition-all relative"
              >
                <Calendar className="w-3.5 h-3.5 text-red-500" />
                <span className="hidden sm:inline">Bookings</span>
              </button>
              <button
                onClick={() => navigate('/customer/profile')}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 transition-all"
              >
                <User className="w-3.5 h-3.5 text-blue-500" />
                <span className="hidden sm:inline">Profile</span>
              </button>
              <button
                onClick={handleLogout}
                className="p-1.5 bg-gray-50 hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-xl text-gray-500 hover:text-red-500 transition-all"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── PREMIUM HERO HEADER ──────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(160deg, #c0392b 0%, #8e2de2 100%)',
        borderBottomLeftRadius: '28px',
        borderBottomRightRadius: '28px',
        padding: '20px 16px 28px',
        position: 'relative',
        // NOTE: no overflow:hidden here — it would clip the search dropdown
      }}>
        {/* Decorative circles — clipped inside their own wrapper */}
        <div style={{position:'absolute',inset:0,overflow:'hidden',borderBottomLeftRadius:'28px',borderBottomRightRadius:'28px',pointerEvents:'none'}}>
          <div style={{position:'absolute',top:'-40px',right:'-40px',width:'140px',height:'140px',borderRadius:'50%',background:'rgba(255,255,255,0.06)'}} />
          <div style={{position:'absolute',bottom:'-20px',left:'-20px',width:'100px',height:'100px',borderRadius:'50%',background:'rgba(255,255,255,0.05)'}} />
        </div>

        {/* Header top row */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px',position:'relative',zIndex:1}}>
          <div>
            <p style={{color:'rgba(255,255,255,0.75)',fontSize:'0.75rem',marginBottom:'2px'}}>
              📍 {location.lat ? (location.address?.split(',')[0] || 'Location Set') : 'No location set'}
            </p>
            <p style={{color:'white',fontSize:'1rem',fontWeight:800,fontFamily:"'Syne',sans-serif"}}>
              Book<span style={{color:'#fbbf24'}}>Your</span>Salons
            </p>
          </div>
          <button
            onClick={() => setShowLocationPicker(true)}
            style={{
              background:'rgba(255,255,255,0.15)',
              border:'1px solid rgba(255,255,255,0.25)',
              borderRadius:'12px',
              padding:'6px 12px',
              color:'white',
              fontSize:'0.75rem',
              fontWeight:600,
              cursor:'pointer',
              display:'flex',alignItems:'center',gap:'4px',
              backdropFilter:'blur(8px)',
            }}
          >
            <MapPin size={12} /> {location.lat ? 'Change' : 'Set Location'}
          </button>
        </div>

        {/* Welcome text */}
        <div style={{marginBottom:'16px',position:'relative',zIndex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
            <span style={{color:'white',fontSize:'1.1rem',fontWeight:700,fontFamily:"'Syne',sans-serif"}}>
              Book your
            </span>
            <RotatingText
              texts={['Haircut ✂️','Facial 💆','Hair Color 🎨','Blowout 💇','Beard Trim 🧔','Spa Day 🧖']}
              mainClassName="px-3 py-1 bg-white/20 text-white font-bold rounded-xl backdrop-blur-sm"
              rotationInterval={2200}
            />
            <span style={{color:'white',fontSize:'1.1rem',fontWeight:700,fontFamily:"'Syne',sans-serif"}}>today</span>
          </div>
        </div>

        {/* Search Bar */}
        <div style={{position:'relative',zIndex:1}}>
          <div style={{
            background:'white',
            borderRadius:'16px',
            display:'flex',
            alignItems:'center',
            gap:'10px',
            padding:'12px 16px',
            boxShadow:'0 8px 24px rgba(0,0,0,0.15)',
          }}>
            <Search size={18} color="#9ca3af" />
            <input
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); handleSmartSearch(e.target.value); }}
              placeholder="Search salons, services, or stylists..."
              data-testid="smart-search-input"
              style={{
                border:'none',outline:'none',flex:1,
                fontSize:'0.88rem',color:'#111827',
                background:'transparent',fontFamily:'inherit',
              }}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setShowSearchResults(false); }} style={{background:'none',border:'none',cursor:'pointer'}}>
                <X size={16} color="#9ca3af" />
              </button>
            )}
          </div>

          {/* Search dropdown */}
          {showSearchResults && (
            <div style={{
              position:'absolute',top:'calc(100% + 8px)',left:0,right:0,
              background:'white',borderRadius:'16px',
              boxShadow:'0 8px 32px rgba(0,0,0,0.15)',
              zIndex:9999,maxHeight:'280px',overflowY:'auto',
              border:'1px solid #f3f4f6',
            }}>
              {isSearching && (
                <div style={{display:'flex',alignItems:'center',gap:'12px',padding:'12px 16px',borderBottom:'1px solid #f9fafb'}}>
                  <div style={{width:'16px',height:'16px',borderRadius:'50%',border:'2px solid #dc2626',borderTopColor:'transparent',animation:'spin 1s linear infinite'}} />
                  <span style={{color:'#6b7280',fontSize:'0.85rem'}}>Searching salons...</span>
                </div>
              )}
              {!isSearching && searchResults.length === 0 && (
                <div style={{padding:'16px',textAlign:'center',color:'#9ca3af',fontSize:'0.85rem'}}>No salons found</div>
              )}
              {searchResults.slice(0,6).map((salon) => (
                <div key={salon.salon_id}
                  style={{padding:'12px 16px',borderBottom:'1px solid #f9fafb',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between'}}
                  onClick={() => { handleViewSalonDetails(salon); setShowSearchResults(false); }}
                  onMouseEnter={e => e.currentTarget.style.background='#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background='white'}
                >
                  <div>
                    <p style={{fontWeight:600,color:'#111827',fontSize:'0.88rem'}}>{salon.salon_name}</p>
                    <p style={{color:'#9ca3af',fontSize:'0.78rem'}}>{salon.area}</p>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:'4px',color:'#f59e0b'}}>
                    <Star size={14} style={{fill:'#f59e0b'}} />
                    <span style={{fontSize:'0.82rem',fontWeight:600}}>{salon.rating || '4.0'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location not set CTA */}
        {!location.lat && (
          <div style={{
            marginTop:'12px',background:'rgba(255,255,255,0.12)',
            borderRadius:'14px',padding:'12px 16px',
            display:'flex',alignItems:'center',justifyContent:'space-between',
            position:'relative',zIndex:1,
          }}>
            <p style={{color:'rgba(255,255,255,0.9)',fontSize:'0.82rem'}}>
              📍 Set location to find nearby salons
            </p>
            <button
              data-testid="hero-choose-location-btn"
              onClick={() => setShowLocationPicker(true)}
              style={{
                background:'white',color:'#dc2626',
                border:'none',borderRadius:'10px',
                padding:'6px 14px',fontWeight:700,
                fontSize:'0.78rem',cursor:'pointer',
              }}
            >
              Set Now
            </button>
          </div>
        )}
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">

        {/* ── STYLE FINDER banner ──────────── */}
        <div
          onClick={() => navigate('/style-finder')}
          className="mb-5 animate-fade-up"
          style={{
            cursor: 'pointer', borderRadius: 18, overflow: 'hidden',
            background: 'linear-gradient(120deg,#7c3aed 0%,#6d28d9 45%,#4c1d95 100%)',
            boxShadow: '0 8px 24px rgba(124,58,237,0.3)',
            display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px',
          }}
        >
          <div style={{ fontSize: 34, flexShrink: 0 }}>✨</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'inline-block', background: '#fff', color: '#7c3aed', fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 20, marginBottom: 4, letterSpacing: '0.03em' }}>
              NEW • AI POWERED
            </div>
            <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, color: '#fff', fontSize: 17, margin: 0, lineHeight: 1.2 }}>
              Find Your Perfect Hairstyle
            </h3>
            <p style={{ color: '#ede9fe', fontSize: 12, margin: '2px 0 0' }}>
              Selfie lo → face shape → best styles suggest
            </p>
          </div>
          <div style={{ color: '#fff', fontSize: 22, flexShrink: 0 }}>→</div>
        </div>

        {/* ── SERVICE CATEGORIES (like screenshot) ──────────── */}
        <div className="mb-5 animate-fade-up">
          <h2 style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:'1rem',color:'#111827',marginBottom:'14px'}}>
            Service Categories
          </h2>
          <div style={{display:'flex',gap:'12px',overflowX:'auto',paddingBottom:'4px',scrollbarWidth:'none'}}>
            {[
              { emoji: '✂️', label: 'Haircuts',   query: 'hair' },
              { emoji: '🎨', label: 'Coloring',   query: 'color' },
              { emoji: '💆', label: 'Facials',    query: 'facial' },
              { emoji: '💅', label: 'Manicures',  query: 'manicure' },
              { emoji: '🧔', label: "Men's Grooming", query: 'beard' },
              { emoji: '🧖', label: 'Spa',        query: 'spa' },
              { emoji: '💇', label: 'Blowout',    query: 'blowout' },
            ].map(({ emoji, label, query }) => (
              <button
                key={label}
                onClick={() => { setSearchQuery(query); handleSmartSearch(query); }}
                style={{
                  display:'flex',flexDirection:'column',alignItems:'center',
                  gap:'8px',flexShrink:0,background:'none',border:'none',
                  cursor:'pointer',padding:'4px 2px',minWidth:'60px',
                }}
              >
                <div style={{
                  width:'56px',height:'56px',borderRadius:'50%',
                  background:'white',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:'24px',
                  boxShadow:'0 2px 12px rgba(0,0,0,0.08)',
                  transition:'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                  onMouseEnter={e => {e.currentTarget.style.transform='scale(1.1)';e.currentTarget.style.boxShadow='0 4px 16px rgba(220,38,38,0.2)';}}
                  onMouseLeave={e => {e.currentTarget.style.transform='scale(1)';e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.08)';}}
                >
                  {emoji}
                </div>
                <span style={{fontSize:'0.7rem',fontWeight:600,color:'#374151',textAlign:'center',whiteSpace:'pre-line',lineHeight:1.2}}>
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Service Reminders */}
        {serviceReminders.length > 0 && (
          <Card className="mb-4 border-blue-200 bg-blue-50" data-testid="service-reminders">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                <Bell className="w-4 h-4" />
                Time for your next visit!
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {serviceReminders.slice(0, 3).map((reminder, idx) => (
                  <div key={idx} className="flex-shrink-0 bg-white p-2 rounded-lg border text-sm">
                    <p className="font-medium">{reminder.service_name}</p>
                    <p className="text-xs text-gray-500">{reminder.salon_name}</p>
                    <p className="text-xs text-blue-600">{reminder.days_since} days ago</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Map View */}
        {location.lat && showMapView && (
          <div className="mb-4 sm:mb-6" style={{position:"relative", zIndex:1}}>
            <NearbySalonsMap
              userLocation={{ lat: location.lat, lng: location.lng }}
              salons={salons}
              radius={radius}
              onSalonSelect={(salon) => handleViewSalonDetails(salon)}
            />
          </div>
        )}

        {/* ✨ Why Choose Us — Premium Slider */}
        <WhyChooseUsSlider sliderIndex={sliderIndex} setSliderIndex={setSliderIndex} />

        {/* Trending Salons Section */}
        {trendingSalons.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-red-600" />
                Trending Near You
              </h2>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
              {trendingSalons.slice(0, 5).map((salon) => (
                <Card 
                  key={salon.salon_id} 
                  className="flex-shrink-0 w-64 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleViewSalonDetails(salon)}
                  data-testid={`trending-salon-${salon.salon_id}`}
                >
                  {salon.photo_url && (
                    <div className="h-32 overflow-hidden rounded-t-lg">
                      <img src={salon.photo_url} alt={salon.salon_name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm">{salon.salon_name}</p>
                        <p className="text-xs text-gray-500">{salon.area}</p>
                      </div>
                      <div className="flex items-center gap-1 bg-green-600 text-white px-1.5 py-0.5 rounded text-xs">
                        <Star className="w-3 h-3 fill-current" />
                        {salon.rating || '4.0'}
                      </div>
                    </div>
                    {salon.recent_bookings > 0 && (
                      <p className="text-xs text-red-600 mt-1">{salon.recent_bookings} bookings this week</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Gender Filter */}
        {userGender && (
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Showing {!userGender || userGender === 'all' ? 'All Salons & Parlours' : userGender === 'male' ? 'Male Salons only' : 'Female Parlours only'}</span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={!userGender || userGender === 'all' ? 'default' : 'outline'}
                  onClick={() => handleGenderChange('all')}
                  className="text-xs sm:text-sm"
                >
                  🏪 All
                </Button>
                <Button
                  size="sm"
                  variant={userGender === 'male' ? 'default' : 'outline'}
                  onClick={() => handleGenderChange('male')}
                  className="text-xs sm:text-sm"
                >
                  ✂️ Male Salons
                </Button>
                <Button
                  size="sm"
                  variant={userGender === 'female' ? 'default' : 'outline'}
                  onClick={() => handleGenderChange('female')}
                  className="text-xs sm:text-sm"
                >
                  💅 Parlours
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Categories */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold">Categories</h2>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-4 mb-6 sm:mb-8">
          <div
            onClick={() => setSelectedCategory('all')}
            className={`p-2 sm:p-4 bg-white rounded-lg shadow-sm cursor-pointer text-center transition-all ${
              selectedCategory === 'all' ? 'ring-2 ring-red-600' : 'hover:shadow-md'
            }`}
          >
            <div className="text-xl sm:text-3xl mb-1 sm:mb-2">🏪</div>
            <div className="font-medium text-xs sm:text-base">All</div>
          </div>
          {categories.map(cat => (
            <div
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`p-2 sm:p-4 bg-white rounded-lg shadow-sm cursor-pointer text-center transition-all ${
                selectedCategory === cat.id ? 'ring-2 ring-red-600' : 'hover:shadow-md'
              }`}
            >
              <div className="text-xl sm:text-3xl mb-1 sm:mb-2">{cat.icon}</div>
              <div className="font-medium text-xs sm:text-base">{cat.name}</div>
            </div>
          ))}
        </div>

        {/* Salons List */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px'}}>
          <h2 style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:'1rem',color:'#111827'}}>
            {filteredSalons.length > 0 ? 'Featured Salons' : 'Salons Near You'}
          </h2>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-28 text-xs rounded-xl border-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">⭐ Top Rated</SelectItem>
              <SelectItem value="price">💰 Lowest Price</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
                <div className="skeleton h-44 w-full" />
                <div className="p-4 space-y-3">
                  <div className="skeleton h-4 w-3/4 rounded-lg" />
                  <div className="skeleton h-3 w-1/2 rounded-lg" />
                  <div className="flex gap-2 mt-2">
                    <div className="skeleton h-6 w-16 rounded-full" />
                    <div className="skeleton h-6 w-20 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredSalons.length === 0 ? (
          <div style={{textAlign:'center',padding:'40px 20px',background:'white',borderRadius:'20px',border:'1px solid #f3f4f6'}}>
            <div style={{fontSize:'40px',marginBottom:'12px'}}>🔍</div>
            <p style={{fontWeight:700,color:'#111827',fontSize:'0.95rem',marginBottom:'6px'}}>
              {salons.length === 0 ? 'No salons in your area yet' : 'No salons match this filter'}
            </p>
            <p style={{color:'#6b7280',fontSize:'0.82rem',marginBottom:'16px',lineHeight:1.6}}>
              {salons.length === 0
                ? `We couldn't find any salons within ${radius}km of your location.`
                : 'Try switching to "All" or clearing your search.'}
            </p>
            <div style={{display:'flex',gap:'8px',justifyContent:'center',flexWrap:'wrap'}}>
              {salons.length === 0 ? (
                <>
                  <button onClick={() => setShowLocationPicker(true)}
                    style={{background:'linear-gradient(135deg,#dc2626,#e11d48)',color:'white',border:'none',
                      borderRadius:'12px',padding:'10px 18px',fontWeight:700,fontSize:'0.8rem',cursor:'pointer'}}>
                    📍 Change Location
                  </button>
                  <button onClick={searchSalons}
                    style={{background:'#f3f4f6',color:'#374151',border:'none',
                      borderRadius:'12px',padding:'10px 18px',fontWeight:700,fontSize:'0.8rem',cursor:'pointer'}}>
                    🔄 Retry
                  </button>
                </>
              ) : (
                <button onClick={() => { handleGenderChange('all'); setSearchQuery(''); }}
                  style={{background:'linear-gradient(135deg,#dc2626,#e11d48)',color:'white',border:'none',
                    borderRadius:'12px',padding:'10px 18px',fontWeight:700,fontSize:'0.8rem',cursor:'pointer'}}>
                  Show All Salons
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {filteredSalons.map((salon, idx) => (
              <div
                key={salon.salon_id}
                data-testid={`salon-card-${salon.salon_id}`}
                onClick={() => handleViewSalonDetails(salon)}
                style={{
                  background:'white',
                  borderRadius:'20px',
                  overflow:'hidden',
                  cursor:'pointer',
                  boxShadow:'0 2px 12px rgba(0,0,0,0.08)',
                  border:'1px solid #f3f4f6',
                  transition:'all 0.25s cubic-bezier(0.16,1,0.3,1)',
                  animationDelay: `${idx * 0.05}s`,
                }}
                className="animate-fade-up"
                onMouseEnter={e => {e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow='0 12px 32px rgba(0,0,0,0.14)';}}
                onMouseLeave={e => {e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.08)';}}
              >
                {/* Image */}
                <div style={{height:'130px',position:'relative',overflow:'hidden'}}>
                  {salon.photo_url ? (
                    <img src={salon.photo_url} alt={salon.salon_name}
                      style={{width:'100%',height:'100%',objectFit:'cover',transition:'transform 0.5s ease'}}
                      onMouseEnter={e => e.target.style.transform='scale(1.05)'}
                      onMouseLeave={e => e.target.style.transform='scale(1)'}
                    />
                  ) : (
                    <div style={{width:'100%',height:'100%',background:'linear-gradient(135deg,#fce7f3,#fff1f2)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <span style={{fontSize:'32px'}}>✂️</span>
                    </div>
                  )}
                  {/* Gradient overlay */}
                  <div style={{position:'absolute',inset:0,background:'linear-gradient(to top,rgba(0,0,0,0.3) 0%,transparent 60%)'}} />
                  {/* Heart */}
                  <button
                    onClick={(e) => toggleFavorite(salon.salon_id, e)}
                    style={{
                      position:'absolute',top:'8px',right:'8px',
                      width:'30px',height:'30px',borderRadius:'50%',
                      background:'rgba(255,255,255,0.9)',border:'none',
                      cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',
                      backdropFilter:'blur(4px)',boxShadow:'0 2px 8px rgba(0,0,0,0.12)',
                    }}
                  >
                    <Heart size={14} style={{fill: favorites.includes(salon.salon_id) ? '#ef4444':'none', color: favorites.includes(salon.salon_id) ? '#ef4444':'#6b7280'}} />
                  </button>
                  {/* Status badge on image */}
                  {salon.current_status === 'closed' && (
                    <div style={{position:'absolute',bottom:'8px',left:'8px',background:'rgba(0,0,0,0.6)',color:'white',fontSize:'0.65rem',fontWeight:700,padding:'2px 8px',borderRadius:'20px'}}>
                      CLOSED
                    </div>
                  )}
                </div>

                {/* Content */}
                <div style={{padding:'12px'}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'4px'}}>
                    <p style={{fontWeight:700,fontSize:'0.85rem',color:'#111827',flex:1,marginRight:'6px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {salon.salon_name}
                    </p>
                  </div>
                  <p style={{color:'#9ca3af',fontSize:'0.72rem',marginBottom:'8px',display:'flex',alignItems:'center',gap:'3px'}}>
                    <MapPin size={10} />{salon.area}
                  </p>
                  {/* Rating + Reviews */}
                  <div style={{display:'flex',alignItems:'center',gap:'4px',marginBottom:'10px'}}>
                    <Star size={12} style={{fill:'#f59e0b',color:'#f59e0b'}} />
                    <span style={{fontSize:'0.78rem',fontWeight:700,color:'#374151'}}>{salon.rating || '4.0'}</span>
                    {salon.review_count > 0 && (
                      <span style={{fontSize:'0.7rem',color:'#9ca3af'}}>| {salon.review_count} reviews</span>
                    )}
                  </div>
                  {/* Book Now button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleViewSalonDetails(salon); }}
                    disabled={salon.current_status === 'closed'}
                    style={{
                      width:'100%',padding:'8px',borderRadius:'12px',border:'none',
                      background: salon.current_status === 'closed'
                        ? '#f3f4f6'
                        : 'linear-gradient(135deg,#dc2626,#e11d48)',
                      color: salon.current_status === 'closed' ? '#9ca3af' : 'white',
                      fontWeight:700,fontSize:'0.78rem',cursor: salon.current_status === 'closed' ? 'not-allowed' : 'pointer',
                      transition:'all 0.2s ease',
                    }}
                    onMouseEnter={e => { if(salon.current_status !== 'closed') e.currentTarget.style.opacity='0.9'; }}
                    onMouseLeave={e => e.currentTarget.style.opacity='1'}
                  >
                    {salon.current_status === 'closed' ? 'Closed' : 'Book Now'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Salon Details Dialog — Premium UI */}
      <Dialog open={showSalonDetails} onOpenChange={setShowSalonDetails}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto p-0" style={{overscrollBehavior:'contain',borderRadius:'20px'}} aria-describedby="salon-detail-desc">
          {/* Accessible title/description for screen readers (visually hidden) */}
          <DialogTitle className="sr-only">{selectedSalon?.salon_name || 'Salon Details'}</DialogTitle>
          <DialogDescription id="salon-detail-desc" className="sr-only">
            Salon services, ratings and booking options
          </DialogDescription>
          {selectedSalon && (
            <div>
              {/* Hero Image */}
              <div style={{position:'relative',height:'200px',background:'linear-gradient(135deg,#dc2626,#7c3aed)',overflow:'hidden'}}>
                {selectedSalon.photo_url ? (
                  <img src={selectedSalon.photo_url} alt={selectedSalon.salon_name}
                    style={{width:'100%',height:'100%',objectFit:'cover'}} />
                ) : (
                  <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'48px'}}>✂️</div>
                )}
                <div style={{position:'absolute',inset:0,background:'linear-gradient(to top,rgba(0,0,0,0.6) 0%,transparent 50%)'}} />
                <div style={{position:'absolute',bottom:'16px',left:'16px',right:'16px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'4px'}}>
                    <h2 style={{color:'white',fontWeight:800,fontSize:'1.2rem',fontFamily:"'Syne',sans-serif"}}>{selectedSalon.salon_name}</h2>
                    {getSalonStatusBadge(selectedSalon)}
                  </div>
                  <p style={{color:'rgba(255,255,255,0.85)',fontSize:'0.78rem',display:'flex',alignItems:'center',gap:'4px'}}>
                    <MapPin size={12}/>{selectedSalon.area}
                  </p>
                </div>
                <button onClick={()=>setShowSalonDetails(false)}
                  style={{position:'absolute',top:'12px',right:'12px',background:'rgba(0,0,0,0.4)',border:'none',borderRadius:'50%',width:'32px',height:'32px',color:'white',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'16px'}}>
                  ✕
                </button>
              </div>

              {/* Stats Row */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'1px',background:'#f3f4f6'}}>
                {[
                  { icon:'⭐', val: selectedSalon.rating || '4.0', lbl:'Rating' },
                  { icon:'💬', val: selectedSalon.review_count || 0, lbl:'Reviews' },
                  { icon:'📍', val: selectedSalon.distance ? `${selectedSalon.distance}km` : 'Nearby', lbl:'Distance' },
                  { icon:'🕐', val: `${selectedSalon.opening_time||'9AM'}`, lbl:'Opens' },
                ].map(({icon,val,lbl}) => (
                  <div key={lbl} style={{background:'white',padding:'12px 8px',textAlign:'center'}}>
                    <div style={{fontSize:'16px',marginBottom:'3px'}}>{icon}</div>
                    <div style={{fontWeight:800,fontSize:'0.82rem',color:'#111827'}}>{val}</div>
                    <div style={{fontSize:'0.62rem',color:'#9ca3af'}}>{lbl}</div>
                  </div>
                ))}
              </div>

              <div style={{padding:'16px 16px 24px'}}>
                {/* Gallery strip */}
                {selectedSalon.photo_gallery?.length > 0 && (
                  <div style={{display:'flex',gap:'8px',overflowX:'auto',marginBottom:'16px',paddingBottom:'4px'}}>
                    {selectedSalon.photo_gallery.map((photo, idx) => (
                      <img key={idx} src={photo.url} alt={`Gallery ${idx+1}`}
                        style={{width:'80px',height:'80px',objectFit:'cover',borderRadius:'12px',flexShrink:0}} />
                    ))}
                  </div>
                )}

                {/* Services */}
                <div style={{marginBottom:'16px'}}>
                  <h4 style={{fontWeight:800,fontSize:'0.9rem',color:'#111827',marginBottom:'10px',fontFamily:"'Syne',sans-serif"}}>Services</h4>
                  <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                    {selectedSalon.services?.map((service) => (
                      <div key={service.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'#fafafa',borderRadius:'14px',padding:'12px 14px',border:'1px solid #f3f4f6'}}>
                        <div>
                          <p style={{fontWeight:700,fontSize:'0.85rem',color:'#111827'}}>{service.name}</p>
                          <p style={{fontSize:'0.75rem',color:'#dc2626',fontWeight:700,marginTop:'2px'}}>
                            {service.displayPrice || (service.priceType === 'range' && service.minPrice && service.maxPrice
                              ? `₹${service.minPrice} - ₹${service.maxPrice}`
                              : `₹${service.price || 0}`)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleBookService(selectedSalon, service)}
                          disabled={selectedSalon.current_status === 'closed'}
                          style={{background: selectedSalon.current_status==='closed' ? '#e5e7eb' : 'linear-gradient(135deg,#dc2626,#e11d48)',
                            color: selectedSalon.current_status==='closed' ? '#9ca3af' : 'white',
                            border:'none',borderRadius:'10px',padding:'7px 14px',fontWeight:700,fontSize:'0.75rem',cursor:'pointer'}}
                        >
                          Book Now
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reviews */}
                {salonReviews.length > 0 && (
                  <div style={{marginBottom:'16px'}}>
                    <h4 style={{fontWeight:800,fontSize:'0.9rem',color:'#111827',marginBottom:'10px',fontFamily:"'Syne',sans-serif"}}>Recent Reviews</h4>
                    {salonReviews.slice(0,3).map((review) => (
                      <div key={review.review_id} style={{background:'#fafafa',borderRadius:'14px',padding:'12px',marginBottom:'8px',border:'1px solid #f3f4f6'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
                          <span style={{fontWeight:700,fontSize:'0.8rem',color:'#111827'}}>{review.customer_name}</span>
                          <div style={{display:'flex',gap:'2px'}}>
                            {[...Array(5)].map((_,i) => (
                              <Star key={i} size={12} style={{color:'#f59e0b',fill: i<review.rating ? '#f59e0b':'none'}} />
                            ))}
                          </div>
                        </div>
                        {review.review_text && <p style={{fontSize:'0.76rem',color:'#6b7280',lineHeight:1.5}}>{review.review_text}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Action buttons — extra bottom margin keeps them clear of
                    the fixed bottom nav on short screens */}
                <div style={{display:'flex',gap:'10px',marginBottom:'8px'}}>
                  <button onClick={()=>setShowReviewDialog(true)}
                    style={{flex:1,background:'#f3f4f6',border:'none',borderRadius:'12px',padding:'11px',fontWeight:700,fontSize:'0.8rem',color:'#374151',cursor:'pointer'}}>
                    ✍️ Write Review
                  </button>
                  {selectedSalon.location?.coordinates && (
                    <button onClick={()=>{const[lng,lat]=selectedSalon.location.coordinates;window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,'_blank');}}
                      style={{flex:1,background:'#eff6ff',border:'none',borderRadius:'12px',padding:'11px',fontWeight:700,fontSize:'0.8rem',color:'#2563eb',cursor:'pointer'}}>
                      🗺️ Directions
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" style={{overscrollBehavior:'contain',padding:'0',borderRadius:'20px'}} aria-describedby="booking-desc">
          <DialogHeader style={{padding:'16px 16px 0'}}>
            <DialogTitle style={{fontFamily:"'Syne',sans-serif",fontWeight:800}}>Book Appointment</DialogTitle>
            <DialogDescription id="booking-desc" className="sr-only">
              Select date, time slot and payment method
            </DialogDescription>
          </DialogHeader>
          {selectedSalon && selectedService && (
            <div style={{padding:'16px',display:'flex',flexDirection:'column',gap:'14px'}}>

              {/* Summary Card */}
              <div style={{background:'linear-gradient(135deg,#fef2f2,#fff5f5)',borderRadius:'16px',padding:'14px',border:'1px solid #fecaca'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <p style={{fontWeight:800,fontSize:'0.9rem',color:'#111827'}}>{selectedSalon.salon_name}</p>
                    <p style={{fontSize:'0.78rem',color:'#6b7280',marginTop:'2px'}}>{selectedService.name}</p>
                  </div>
                  <span style={{background:'linear-gradient(135deg,#dc2626,#e11d48)',color:'white',fontWeight:800,fontSize:'0.88rem',padding:'4px 12px',borderRadius:'20px'}}>
                    {selectedService.displayPrice || (selectedService.priceType === 'range' && selectedService.minPrice
                      ? `₹${selectedService.minPrice} - ₹${selectedService.maxPrice}`
                      : `₹${selectedService.price || 0}`)}
                  </span>
                </div>
              </div>

              {/* Date */}
              <div>
                <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'0.8rem',fontWeight:700,color:'#374151',marginBottom:'8px'}}>
                  <Calendar size={14}/> Select Date
                </label>
                <Input type="date" value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{borderRadius:'12px',fontSize:'0.85rem'}}
                />
              </div>

              {/* Slots */}
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'8px'}}>
                  <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'0.8rem',fontWeight:700,color:'#374151'}}>
                    <Clock size={14}/> Select Time Slot
                  </label>
                  <button onClick={()=>{const date=selectedDate?new Date(selectedDate).toISOString().split('T')[0]:new Date().toISOString().split('T')[0];fetchSlots(selectedSalon.salon_id,date);}}
                    style={{fontSize:'0.7rem',color:'#dc2626',fontWeight:600,background:'none',border:'none',cursor:'pointer'}}>
                    🔄 Refresh
                  </button>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',maxHeight:'180px',overflowY:'auto'}}>
                  {availableSlots.length === 0 ? (
                    <div style={{gridColumn:'span 4',textAlign:'center',padding:'20px',color:'#9ca3af',fontSize:'0.82rem'}}>
                      Loading slots...
                    </div>
                  ) : availableSlots.map((slot,idx) => {
                    const isSelected = selectedSlot === slot.time;
                    return (
                      <button key={slot.time}
                        onClick={() => slot.isAvailable && handleSlotSelect(slot.time)}
                        disabled={!slot.isAvailable}
                        style={{
                          padding:'8px 4px',borderRadius:'10px',border:`1.5px solid ${isSelected?'#dc2626':slot.isAvailable?'#e5e7eb':'#f3f4f6'}`,
                          background: isSelected?'linear-gradient(135deg,#dc2626,#e11d48)':slot.isAvailable?'white':'#f9fafb',
                          color: isSelected?'white':slot.isAvailable?'#374151':'#d1d5db',
                          cursor:slot.isAvailable?'pointer':'not-allowed',
                          fontSize:'0.7rem',fontWeight:700,textAlign:'center',
                          transition:'all 0.15s ease',
                        }}>
                        {slot.time}
                        {slot.isPast && <div style={{fontSize:'8px',color:'#9ca3af'}}>Past</div>}
                        {!slot.isAvailable && !slot.isPast && <div style={{fontSize:'8px',color:'#ef4444'}}>Full</div>}
                        {slot.isAvailable && slot.available > 1 && <div style={{fontSize:'8px',color:'#16a34a'}}>{slot.available} left</div>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Payment */}
              <div>
                <label style={{fontSize:'0.8rem',fontWeight:700,color:'#374151',display:'block',marginBottom:'8px'}}>Payment Method</label>
                <div style={{display:'flex',gap:'8px'}}>
                  <button onClick={()=>setPaymentMethod('pay_at_salon')}
                    style={{flex:1,padding:'10px',borderRadius:'12px',border:`2px solid ${paymentMethod==='pay_at_salon'?'#dc2626':'#e5e7eb'}`,
                      background:paymentMethod==='pay_at_salon'?'#fef2f2':'white',
                      color:paymentMethod==='pay_at_salon'?'#dc2626':'#6b7280',fontWeight:700,fontSize:'0.75rem',cursor:'pointer'}}>
                    💳 Pay at Salon
                  </button>
                  <button onClick={()=>setPaymentMethod('online')}
                    style={{flex:1,padding:'10px',borderRadius:'12px',border:`2px solid ${paymentMethod==='online'?'#dc2626':'#e5e7eb'}`,
                      background:paymentMethod==='online'?'#fef2f2':'white',
                      color:paymentMethod==='online'?'#dc2626':'#6b7280',fontWeight:700,fontSize:'0.75rem',cursor:'pointer'}}>
                    📱 Pay Online
                  </button>
                </div>
              </div>

              {/* Confirm */}
              <button onClick={createBooking} disabled={loading}
                style={{width:'100%',background:loading?'#e5e7eb':'linear-gradient(135deg,#dc2626,#e11d48)',
                  color:loading?'#9ca3af':'white',border:'none',borderRadius:'14px',padding:'14px',
                  fontWeight:800,fontSize:'0.9rem',cursor:loading?'not-allowed':'pointer',
                  boxShadow:loading?'none':'0 4px 16px rgba(220,38,38,0.3)',
                  transition:'all 0.2s ease'}}>
                {loading ? '⏳ Processing...' : '✅ Confirm Booking'}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bookings Dialog */}
      <Dialog open={showBookings} onOpenChange={setShowBookings}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" style={{overscrollBehavior:'contain',padding:'0',borderRadius:'20px'}} aria-describedby="my-bookings-desc">
          <DialogTitle className="sr-only">My Bookings</DialogTitle>
          <DialogDescription id="my-bookings-desc" className="sr-only">
            Your upcoming and past salon bookings
          </DialogDescription>
          <div style={{padding:'16px 16px 0',position:'sticky',top:0,background:'white',zIndex:1,borderBottom:'1px solid #f3f4f6',marginBottom:'4px'}}>
            <h2 style={{fontWeight:800,fontSize:'1rem',color:'#111827',fontFamily:"'Syne',sans-serif"}}>📋 My Bookings</h2>
          </div>
          <Tabs defaultValue="upcoming" style={{padding:'0 16px 16px'}}>
            <TabsList className="grid w-full grid-cols-2" style={{marginBottom:'12px',borderRadius:'12px'}}>
              <TabsTrigger value="upcoming" style={{borderRadius:'10px',fontWeight:700}}>
                Upcoming ({bookings.filter(b=>{const d=b.date||b.booking_date;return new Date(d)>=new Date()&&b.status!=='cancelled'&&b.status!=='completed';}).length})
              </TabsTrigger>
              <TabsTrigger value="past" style={{borderRadius:'10px',fontWeight:700}}>
                Past ({bookings.filter(b=>{const d=b.date||b.booking_date;return new Date(d)<new Date()||b.status==='completed';}).length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="upcoming">
              {bookings.filter(b => {
                const bookingDate = b.date || b.booking_date;
                return new Date(bookingDate) >= new Date() && b.status !== 'cancelled' && b.status !== 'completed';
              }).length === 0 ? (
                <div style={{textAlign:'center',padding:'32px',color:'#9ca3af'}}>
                  <div style={{fontSize:'32px',marginBottom:'8px'}}>📅</div>
                  <p style={{fontSize:'0.85rem'}}>No upcoming bookings</p>
                </div>
              ) : bookings.filter(b => {
                const bookingDate = b.date || b.booking_date;
                return new Date(bookingDate) >= new Date() && b.status !== 'cancelled' && b.status !== 'completed';
              }).map((booking) => {
                                  const bkDate = booking.date || booking.booking_date;
                  const statusColor = booking.status==='confirmed'?'#16a34a':booking.status==='pending'?'#d97706':'#6b7280';
                  const statusBg = booking.status==='confirmed'?'#dcfce7':booking.status==='pending'?'#fef3c7':'#f3f4f6';
                  const borderColor = booking.status==='confirmed'?'#16a34a':booking.status==='pending'?'#f59e0b':'#e5e7eb';
                  return (
                    <div key={booking.bookingId||booking.booking_id||booking.id}
                      style={{background:'white',borderRadius:'16px',padding:'16px',
                        borderLeft:`4px solid ${borderColor}`,boxShadow:'0 2px 10px rgba(0,0,0,0.06)',
                        border:`1px solid #f3f4f6`,borderLeftWidth:'4px',borderLeftColor:borderColor,marginBottom:'10px'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'10px'}}>
                        <div>
                          <p style={{fontWeight:800,fontSize:'0.9rem',color:'#111827'}}>{booking.salonName||booking.salon_name}</p>
                          <p style={{fontSize:'0.78rem',color:'#6b7280',marginTop:'2px'}}>{booking.serviceName||booking.service_name}</p>
                        </div>
                        <span style={{background:statusBg,color:statusColor,fontSize:'0.65rem',fontWeight:700,padding:'4px 10px',borderRadius:'20px'}}>
                          {booking.status==='confirmed'?'✅ Confirmed':booking.status==='pending'?'⏳ Pending':booking.status.toUpperCase()}
                        </span>
                      </div>
                      <div style={{display:'flex',gap:'16px',marginBottom:'10px'}}>
                        <span style={{fontSize:'0.75rem',color:'#6b7280',display:'flex',alignItems:'center',gap:'4px'}}>
                          📅 <b style={{color:'#374151'}}>{bkDate}</b>
                        </span>
                        <span style={{fontSize:'0.75rem',color:'#6b7280',display:'flex',alignItems:'center',gap:'4px'}}>
                          ⏰ <b style={{color:'#374151'}}>{booking.slotTime||booking.slot_time}</b>
                        </span>
                      </div>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <span style={{fontWeight:800,color:'#dc2626',fontSize:'0.9rem'}}>₹{booking.servicePrice||booking.service_price}</span>
                        {(booking.status==='confirmed'||booking.status==='pending') && (
                          <button onClick={()=>cancelBooking(booking.bookingId||booking.booking_id||booking.id)}
                            style={{background:'#fef2f2',border:'1px solid #fecaca',color:'#dc2626',borderRadius:'10px',
                              padding:'6px 12px',fontSize:'0.72rem',fontWeight:700,cursor:'pointer'}}>
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  );
              })}
            </TabsContent>
            <TabsContent value="past">
              {bookings.filter(b => {
                const bookingDate = b.date || b.booking_date;
                return new Date(bookingDate) < new Date() || b.status === 'completed';
              }).length === 0 ? (
                <div style={{textAlign:'center',padding:'32px',color:'#9ca3af'}}>
                  <div style={{fontSize:'32px',marginBottom:'8px'}}>🕐</div>
                  <p style={{fontSize:'0.85rem'}}>No past bookings</p>
                </div>
              ) : bookings.filter(b => {
                const bookingDate = b.date || b.booking_date;
                return new Date(bookingDate) < new Date() || b.status === 'completed';
              }).map((booking) => {
                                const bkDate = booking.date || booking.booking_date;
                return (
                  <div key={booking.bookingId||booking.booking_id||booking.id}
                    style={{background:'#fafafa',borderRadius:'16px',padding:'16px',
                      borderLeft:'4px solid #9ca3af',border:'1px solid #f3f4f6',borderLeftWidth:'4px',borderLeftColor:'#9ca3af',marginBottom:'10px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'8px'}}>
                      <div>
                        <p style={{fontWeight:800,fontSize:'0.88rem',color:'#374151'}}>{booking.salonName||booking.salon_name}</p>
                        <p style={{fontSize:'0.75rem',color:'#9ca3af',marginTop:'2px'}}>{booking.serviceName||booking.service_name}</p>
                      </div>
                      <span style={{background:booking.status==='completed'?'#eff6ff':'#f3f4f6',
                        color:booking.status==='completed'?'#2563eb':'#6b7280',
                        fontSize:'0.65rem',fontWeight:700,padding:'4px 10px',borderRadius:'20px'}}>
                        {booking.status==='completed'?'✓ Done':booking.status?.toUpperCase()}
                      </span>
                    </div>
                    <div style={{display:'flex',gap:'12px',marginBottom:'8px'}}>
                      <span style={{fontSize:'0.72rem',color:'#9ca3af'}}>📅 {bkDate}</span>
                      <span style={{fontSize:'0.72rem',color:'#9ca3af'}}>⏰ {booking.slotTime||booking.slot_time}</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontWeight:700,color:'#6b7280',fontSize:'0.82rem'}}>₹{booking.servicePrice||booking.service_price}</span>
                      <button onClick={()=>{setSelectedSalon({salon_id:booking.salonId||booking.salon_id,salon_name:booking.salonName||booking.salon_name});setShowReviewDialog(true);}}
                        style={{background:'#fafafa',border:'1px solid #e5e7eb',color:'#374151',borderRadius:'10px',
                          padding:'5px 10px',fontSize:'0.7rem',fontWeight:700,cursor:'pointer'}}>
                        ⭐ Review
                      </button>
                    </div>
                  </div>
                );
              })}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Write a Review</DialogTitle>
            <DialogDescription>{selectedSalon?.salon_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewData({ ...reviewData, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= reviewData.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Your Review (Optional)</label>
              <textarea
                className="w-full p-3 border rounded-lg resize-none"
                rows={4}
                placeholder="Share your experience..."
                value={reviewData.review_text}
                onChange={(e) => setReviewData({ ...reviewData, review_text: e.target.value })}
              />
            </div>
            <Button className="w-full" onClick={submitReview}>
              Submit Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={location.lat ? { lat: location.lat, lng: location.lng } : null}
        title="Choose Your Location"
      />

      {/* ✨ Premium Bottom Nav — hidden while any dialog is open so it
          never covers dialog action buttons (Write Review / Directions) */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        zIndex: 40,
        display: (showSalonDetails || showBooking || showBookings || showReviewDialog || showLocationPicker)
          ? 'none' : 'flex',
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(0,0,0,0.08)',
        padding: '8px 16px 12px',
        justifyContent: 'space-around', alignItems: 'center',
      }}>
        {[
          { icon: <MapPin className="w-5 h-5" />, label: 'Location', action: () => setShowLocationPicker(true) },
          { icon: <Map className="w-5 h-5" />, label: 'Map', action: () => { setShowMapView(v => !v); window.scrollTo({top:0,behavior:'smooth'}); } },
          { icon: <Calendar className="w-5 h-5" />, label: 'Bookings', action: fetchBookings },
          { icon: <User className="w-5 h-5" />, label: 'Profile', action: () => navigate('/customer/profile') },
          { icon: <HelpCircle className="w-5 h-5" />, label: 'Help', action: () => navigate('/help') },
        ].map(({ icon, label, action }, i) => (
          <button
            key={i}
            onClick={action}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '3px', background: 'none', border: 'none', cursor: 'pointer',
              color: '#dc2626', padding: '4px 12px', borderRadius: '12px',
              transition: 'all 0.18s ease',
              fontSize: '10px', fontWeight: 600, letterSpacing: '0.02em',
            }}
            onTouchStart={e => e.currentTarget.style.transform = 'scale(0.92)'}
            onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Bottom padding */}
      <div style={{ height: '80px' }} />

      {/* 🤖 AI Booking Agent */}
      <AIChatWidget
        userPhone={localStorage.getItem('userPhone') || ''}
        userLocation={location.lat ? { lat: location.lat, lng: location.lng } : null}
        onBookingComplete={(data) => {
          if (data.bookingId) {
            toast.success('AI se booking ho gayi! 🎉', {
              description: `Booking ID: ${data.bookingId}`,
              duration: 6000,
            });
            fetchBookings();
          }
        }}
      />

    </div>
  );
}

export default CustomerHome;
